import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TextSizeSlider from '../components/TextSizeSlider';
import WeeklyDigestCard from '../components/WeeklyDigestCard';
import TrialNudgeBanner from '../components/TrialNudgeBanner';
import ReferralCard from '../components/ReferralCard';
import LlmQuotaBadge from '../components/LlmQuotaBadge';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Code2, BookOpen, Trophy, Clock, ChevronRight, Sun, Moon, 
  LogOut, User, Settings, LayoutDashboard, GraduationCap,
  Play, TrendingUp, Target, Zap, Award, Bell, AlertTriangle,
  FileText, PenLine, Brain, BarChart3, Compass, FlaskConical,
  ClipboardList, Swords, Wrench, ChevronDown, Monitor, Flame, Calendar, Film, CreditCard
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../components/ui/popover';
import { courseRequestsApi, statsApi, notificationsApi, gradeAllocationsApi, gradeChangeRequestApi, studyPlanApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useFeatureFlags } from '../context/FeatureFlagContext';

const languageColors = {
  python: 'bg-blue-500',
  sql: 'bg-orange-500',
  c: 'bg-gray-500',
  cpp: 'bg-purple-500',
  java: 'bg-red-500',
  html: 'bg-pink-500',
  php: 'bg-indigo-500',
  bash: 'bg-green-500',
  shell: 'bg-green-500',
  powershell: 'bg-cyan-500'
};

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [gradeRequired, setGradeRequired] = useState(false);
  const [gradeValue, setGradeValue] = useState('');
  const [settingGrade, setSettingGrade] = useState(false);
  const [showGradeChangeModal, setShowGradeChangeModal] = useState(false);
  const [gradeChangeGrade, setGradeChangeGrade] = useState('');
  const [gradeChangeReason, setGradeChangeReason] = useState('');
  const [submittingGradeChange, setSubmittingGradeChange] = useState(false);
  const [myGradeRequests, setMyGradeRequests] = useState([]);
  const [currentGrade, setCurrentGrade] = useState(null);

  const isAdmin = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);
  const { isEnabled } = useFeatureFlags();

  useEffect(() => {
    fetchData();
    if (!isAdmin) {
      // Phase 40.6 — prefer the onboarding class_level, fall back to legacy
      // `grade` field so students never see stale data.
      if (user?.class_level) setCurrentGrade(user.class_level);
      gradeAllocationsApi.getStudentContent().then(data => {
        if (data.grade_required && !user?.class_level) setGradeRequired(true);
        if (data.grade) setCurrentGrade(data.grade);
      }).catch(() => {});
      gradeChangeRequestApi.getMine().then(setMyGradeRequests).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetGrade = async () => {
    if (!gradeValue) return;
    setSettingGrade(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/users/${user.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ grade: parseInt(gradeValue), class_level: parseInt(gradeValue) }),
        credentials: 'include'
      });
      setGradeRequired(false);
      setCurrentGrade(parseInt(gradeValue));
      // Phase 40.10 — refresh AuthContext so competitive-exams gate immediately
      // sees the new class_level without a page reload.
      try { await checkAuth(); } catch { /* ignore */ }
      toast.success('Grade set successfully!');
    } catch (err) {
      toast.error('Failed to set grade');
    } finally { setSettingGrade(false); }
  };

  const handleGradeChangeRequest = async () => {
    if (!gradeChangeGrade || !gradeChangeReason.trim()) return;
    setSubmittingGradeChange(true);
    try {
      await gradeChangeRequestApi.create({
        requested_grade: parseInt(gradeChangeGrade),
        reason: gradeChangeReason.trim()
      });
      toast.success('Grade change request submitted! An admin will review it.');
      setShowGradeChangeModal(false);
      setGradeChangeGrade('');
      setGradeChangeReason('');
      const updated = await gradeChangeRequestApi.getMine();
      setMyGradeRequests(updated);
    } catch (err) {
      toast.error(err.message || 'Failed to submit request');
    } finally { setSubmittingGradeChange(false); }
  };

  const pendingRequest = myGradeRequests.find(r => r.status === 'pending');
  const latestResolved = myGradeRequests.find(r => r.status !== 'pending');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
    // Students/parents: poll for study-plan reminders + streak
    fetchNotifications();
    studyPlanApi.streak().then(r => setStreak(r.streak || 0)).catch(() => {});
    const interval = setInterval(() => {
      fetchNotifications();
      studyPlanApi.streak().then(r => setStreak(r.streak || 0)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [statsData, coursesData] = await Promise.all([
        statsApi.getDashboard(),
        courseRequestsApi.getMyCourses()
      ]);
      setStats(statsData);
      setMyCourses(coursesData);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const data = await notificationsApi.get();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'course_request') {
      navigate('/admin/requests');
    } else if (notification.type === 'study_plan_reminder' || notification.link) {
      navigate(notification.link || '/study-plan');
    }
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    org_admin: 'School Admin',
    faculty: 'Faculty',
    org_student: 'Student',
    independent_student: 'Independent Learner'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="student-dashboard">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="gradient-blob w-64 h-64 bg-secondary/20 bottom-0 left-1/4" />
      </div>

      {/* Header */}
      <header className="relative z-10 glass border-b border-border/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
              <Link to="/dashboard" className="text-sm font-medium text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors" data-testid="nav-dashboard">
                Dashboard
              </Link>

              {(isEnabled('module_courses') || isEnabled('module_visual_labs')) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1" data-testid="nav-courses-labs">
                      Courses & Labs <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[240px]" data-testid="nav-courses-labs-dropdown">
                    {isEnabled('module_courses') && (
                      <DropdownMenuItem asChild>
                        <Link to="/courses" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-browse-courses">
                          <BookOpen className="w-4 h-4 text-blue-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Browse Courses</p>
                            <p className="text-[11px] text-muted-foreground">Explore all available courses</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isEnabled('module_visual_labs') && (
                      <DropdownMenuItem asChild>
                        <Link to="/labs" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-visual-labs">
                          <FlaskConical className="w-4 h-4 text-emerald-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Visual Labs</p>
                            <p className="text-[11px] text-muted-foreground">Interactive lab experiments</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/storyteller" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-storyteller">
                        <BookOpen className="w-4 h-4 text-fuchsia-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Concept Simplification</p>
                          <p className="text-[11px] text-muted-foreground">10 teachers explain Class 5-12 tough concepts</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/study-material" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-study-material">
                        <Brain className="w-4 h-4 text-purple-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Study Material</p>
                          <p className="text-[11px] text-muted-foreground">AI-powered notes & examples</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {(isEnabled('module_board_exams') || isEnabled('module_competitive_exams')) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1" data-testid="nav-exams">
                      Exams <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[280px]" data-testid="nav-exams-dropdown">
                    {isEnabled('module_board_exams') && (
                      <DropdownMenuItem asChild>
                        <Link to="/exam-module" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-board-exams">
                          <ClipboardList className="w-4 h-4 text-amber-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Board Exam Simulators</p>
                            <p className="text-[11px] text-muted-foreground">Practice board exam papers</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isEnabled('module_competitive_exams') && (
                      <DropdownMenuItem asChild>
                        <Link to="/competitive-exams" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-competitive-exams">
                          <Swords className="w-4 h-4 text-red-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Competitive Exam Simulators</p>
                            <p className="text-[11px] text-muted-foreground">JEE, NEET, UPSC & more</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {(isEnabled('module_explorer') || isEnabled('module_visual_labs')) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1" data-testid="nav-tools">
                      Tools <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[240px]" data-testid="nav-tools-dropdown">
                    {isEnabled('module_explorer') && (
                      <DropdownMenuItem asChild>
                        <Link to="/explorer" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-explorer">
                          <Compass className="w-4 h-4 text-violet-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Explorer</p>
                            <p className="text-[11px] text-muted-foreground">Discover learning paths</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isEnabled('module_visual_labs') && (
                      <DropdownMenuItem asChild>
                        <Link to="/lab-progress" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-my-progress">
                          <BarChart3 className="w-4 h-4 text-cyan-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">My Progress</p>
                            <p className="text-[11px] text-muted-foreground">Track your lab achievements</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/bot-exam" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-bot-exam">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Bot Exam Demo</p>
                          <p className="text-[11px] text-muted-foreground">AI bot attempts our exams live</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Exam Utilities — Phase 36 nav reorg */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1" data-testid="nav-exam-utilities">
                    Exam Utilities <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[260px]" data-testid="nav-exam-utilities-dropdown">
                  <DropdownMenuItem asChild>
                    <Link to="/study-plan" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-study-plan">
                      <Calendar className="w-4 h-4 text-indigo-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Daily Learning Plan</p>
                        <p className="text-[11px] text-muted-foreground">Your coaching-class schedule · streak tracker</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/daily-practice" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-daily-practice">
                      <Flame className="w-4 h-4 text-orange-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Daily Practice</p>
                        <p className="text-[11px] text-muted-foreground">15 Qs/subject · keep your streak</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/performance-hub" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-performance-hub">
                      <BarChart3 className="w-4 h-4 text-indigo-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Performance Hub</p>
                        <p className="text-[11px] text-muted-foreground">Weak spots, progress, trends</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/error-book" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-error-book">
                      <Target className="w-4 h-4 text-rose-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Error Book</p>
                        <p className="text-[11px] text-muted-foreground">Every wrong answer, one place</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/revision" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-revision-hub">
                      <BookOpen className="w-4 h-4 text-emerald-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Revision Hub</p>
                        <p className="text-[11px] text-muted-foreground">Formulas · 2-page notes · flashcards</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/rank-predictor" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-rank-predictor">
                      <Target className="w-4 h-4 text-amber-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Rank & College Predictor</p>
                        <p className="text-[11px] text-muted-foreground">Score → AIR → shortlisted colleges</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/aits" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-aits">
                      <Swords className="w-4 h-4 text-red-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">All-India Test Series</p>
                        <p className="text-[11px] text-muted-foreground">Pan-India tests · live leaderboard</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {isEnabled('module_classroom') && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1" data-testid="nav-classroom">
                      Classroom <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[240px]" data-testid="nav-classroom-dropdown">
                    <DropdownMenuItem asChild>
                      <Link to="/classroom" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-classroom-hub">
                        <GraduationCap className="w-4 h-4 text-blue-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">My Classes</p>
                          <p className="text-[11px] text-muted-foreground">Manage classes & assignments</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/live-classroom" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" data-testid="nav-live-classroom">
                        <Monitor className="w-4 h-4 text-green-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Live Classroom</p>
                          <p className="text-[11px] text-muted-foreground">Video + whiteboard + notes</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isEnabled('module_classroom') && (
                <Link to="/suite" className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md hover:bg-accent transition-colors" data-testid="nav-suite">
                  Suite
                </Link>
              )}
            </nav>

            <div className="flex items-center gap-3">
              {/* Study Streak Badge — students/parents */}
              {!isAdmin && streak > 0 && (
                <Link
                  to="/study-plan"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 transition-colors"
                  data-testid="header-streak-badge"
                  title="Open daily plan"
                >
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-bold text-orange-600 dark:text-orange-400">{streak}</span>
                  <span className="text-[10px] text-orange-600/80 dark:text-orange-400/80 hidden md:inline">day streak</span>
                </Link>
              )}
              {/* Notification Bell */}
              {(
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full relative"
                      data-testid="notifications-btn"
                    >
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0">
                    <div className="p-3 border-b border-border">
                      <h4 className="font-semibold">Notifications</h4>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground text-sm">
                          No new notifications
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className="p-3 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Bell className="w-4 h-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{notification.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{notification.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(notification.timestamp).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-2 border-t border-border flex gap-1">
                        {isAdmin ? (
                          <Button 
                            variant="ghost" 
                            className="flex-1 text-sm"
                            onClick={() => navigate('/admin/requests')}
                          >
                            View All Requests
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            className="flex-1 text-sm"
                            onClick={() => navigate('/study-plan')}
                            data-testid="open-study-plan-btn"
                          >
                            Open Daily Plan
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          className="text-sm text-muted-foreground"
                          data-testid="mark-all-read-btn"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await notificationsApi.markAllRead();
                              setNotifications([]);
                              setUnreadCount(0);
                              toast.success('All notifications dismissed');
                            } catch { toast.error('Failed'); }
                          }}
                        >
                          Mark All Read
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-dashboard"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <TextSizeSlider />

              <LlmQuotaBadge />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{roleLabels[user?.role]}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="billing-menu-item">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Upgrade & Billing
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin/aits')} data-testid="nav-admin-aits">
                      <Swords className="w-4 h-4 mr-2" />
                      AITS Scheduler
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Phase 40.13 — Set-Your-Grade banner removed; the /onboarding
             questionnaire is now the single source of truth for class/board.
             ProtectedRoute will bounce users here before showing the app. */}

        {/* Grade Change Request Card - for students with grade already set */}
        {!gradeRequired && !isAdmin && currentGrade && (
          <div className="mb-6 p-4 rounded-xl border border-border bg-card" data-testid="grade-change-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {user?.board && <span className="text-primary">{user.board}</span>}
                    {user?.board && ' — '}
                    Current Grade: <span className="text-primary">Class {currentGrade}</span>
                    {user?.education_language && <span className="text-muted-foreground ml-2">({user.education_language === 'en' ? 'English' : user.education_language === 'hi' ? 'Hindi' : user.education_language})</span>}
                  </p>
                  {pendingRequest ? (
                    <p className="text-xs text-amber-500 flex items-center gap-1" data-testid="grade-request-pending">
                      <Clock className="w-3 h-3" /> Request pending — Change to Class {pendingRequest.requested_grade}
                    </p>
                  ) : latestResolved?.status === 'rejected' ? (
                    <p className="text-xs text-red-500" data-testid="grade-request-rejected">
                      Last request rejected{latestResolved.admin_note ? `: ${latestResolved.admin_note}` : ''}
                    </p>
                  ) : latestResolved?.status === 'approved' ? (
                    <p className="text-xs text-green-500" data-testid="grade-request-approved">
                      Last request approved
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Grade is locked. Request a change if needed.</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGradeChangeModal(true)}
                disabled={!!pendingRequest}
                data-testid="request-grade-change-btn"
              >
                {pendingRequest ? 'Pending...' : 'Request Change'}
              </Button>
            </div>
          </div>
        )}

        {/* Grade Change Request Modal */}
        <Dialog open={showGradeChangeModal} onOpenChange={setShowGradeChangeModal}>
          <DialogContent className="sm:max-w-md" data-testid="grade-change-modal">
            <DialogHeader>
              <DialogTitle>Request Grade Change</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Your current grade is <strong>Class {currentGrade}</strong>. Select your new grade and provide a reason.</p>
              <div>
                <label className="text-sm font-medium mb-1 block">New Grade</label>
                <Select value={gradeChangeGrade} onValueChange={setGradeChangeGrade}>
                  <SelectTrigger data-testid="grade-change-select">
                    <SelectValue placeholder="Select new class" />
                  </SelectTrigger>
                  <SelectContent>
                    {[6,7,8,9,10,11,12].filter(g => g !== currentGrade).map(g => (
                      <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Reason</label>
                <Textarea
                  placeholder="e.g., I was promoted to the next class..."
                  value={gradeChangeReason}
                  onChange={(e) => setGradeChangeReason(e.target.value)}
                  rows={3}
                  data-testid="grade-change-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGradeChangeModal(false)}>Cancel</Button>
              <Button
                onClick={handleGradeChangeRequest}
                disabled={!gradeChangeGrade || !gradeChangeReason.trim() || submittingGradeChange}
                data-testid="grade-change-submit"
              >
                {submittingGradeChange ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted-foreground">
            {user?.role === 'independent_student' 
              ? 'Continue your coding journey with interactive labs.'
              : user?.role === 'org_student'
              ? 'Track your progress and complete your school assignments.'
              : 'Manage courses and track student progress.'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {['org_student', 'independent_student'].includes(user?.role) ? (
            <>
              <div className="stat-card">
                <div className="stat-card-icon bg-primary/10 mb-4">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <div className="text-2xl font-bold">{stats.enrolled_courses || 0}</div>
                <div className="text-sm text-muted-foreground">Active Courses</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-green-500/10 mb-4">
                  <Trophy className="w-6 h-6 text-green-500" />
                </div>
                <div className="text-2xl font-bold">{stats.completed_courses || 0}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-orange-500/10 mb-4">
                  <Clock className="w-6 h-6 text-orange-500" />
                </div>
                <div className="text-2xl font-bold">{stats.pending_requests || 0}</div>
                <div className="text-sm text-muted-foreground">Pending</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-secondary/10 mb-4">
                  <TrendingUp className="w-6 h-6 text-secondary" />
                </div>
                <div className="text-2xl font-bold">{Math.round(stats.avg_progress || 0)}%</div>
                <div className="text-sm text-muted-foreground">Avg Progress</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-card-icon bg-primary/10 mb-4">
                  <GraduationCap className="w-6 h-6 text-primary" />
                </div>
                <div className="text-2xl font-bold">{stats.total_schools || stats.school_students || stats.my_courses || 0}</div>
                <div className="text-sm text-muted-foreground">
                  {user?.role === 'super_admin' ? 'Total Schools' : user?.role === 'org_admin' ? 'Students' : 'My Courses'}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-secondary/10 mb-4">
                  <User className="w-6 h-6 text-secondary" />
                </div>
                <div className="text-2xl font-bold">{stats.total_users || stats.school_faculty || stats.total_chapters || 0}</div>
                <div className="text-sm text-muted-foreground">
                  {user?.role === 'super_admin' ? 'Total Users' : user?.role === 'org_admin' ? 'Faculty' : 'Total Chapters'}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-green-500/10 mb-4">
                  <BookOpen className="w-6 h-6 text-green-500" />
                </div>
                <div className="text-2xl font-bold">{stats.total_courses || stats.active_enrollments || 0}</div>
                <div className="text-sm text-muted-foreground">
                  {user?.role === 'super_admin' ? 'Total Courses' : 'Active Enrollments'}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon bg-orange-500/10 mb-4">
                  <Target className="w-6 h-6 text-orange-500" />
                </div>
                <div className="text-2xl font-bold">{stats.pending_requests || 0}</div>
                <div className="text-sm text-muted-foreground">Pending Requests</div>
              </div>
            </>
          )}
        </div>

        {/* Trial ending nudge — students only, from day 5 onwards */}
        {['org_student', 'independent_student'].includes(user?.role) && <TrialNudgeBanner />}

        {/* Weekly Progress Digest — students only */}
        {['org_student', 'independent_student'].includes(user?.role) && (
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WeeklyDigestCard />
            <ReferralCard />
          </div>
        )}

        {/* My Courses Section */}
        {['org_student', 'independent_student'].includes(user?.role) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">My Courses</h2>
              <Link to="/courses">
                <Button variant="outline" className="rounded-full" data-testid="browse-courses-btn">
                  Browse All Courses
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="p-6 rounded-xl bg-card border border-border">
                    <div className="skeleton h-4 w-20 mb-4" />
                    <div className="skeleton h-6 w-3/4 mb-2" />
                    <div className="skeleton h-4 w-full mb-4" />
                    <div className="skeleton h-2 w-full" />
                  </div>
                ))}
              </div>
            ) : myCourses.length === 0 ? (
              <div className="empty-state py-16 rounded-xl bg-card border border-border">
                <BookOpen className="empty-state-icon w-16 h-16" />
                <h3 className="text-lg font-semibold mb-2">No courses yet</h3>
                <p className="text-muted-foreground mb-4">Start your learning journey by enrolling in a course.</p>
                <Link to="/courses">
                  <Button className="btn-primary rounded-full" data-testid="enroll-first-course-btn">
                    <Zap className="w-4 h-4 mr-2" />
                    Browse Courses
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myCourses.map((enrollment) => {
                  const isCompleted = enrollment.status === 'completed';
                  const isExamPending = enrollment.status === 'exam_pending';
                  
                  return (
                    <div
                      key={enrollment.request_id}
                      className="course-card group"
                      onClick={() => navigate(`/lab/${enrollment.course_id}`)}
                      data-testid={`course-card-${enrollment.course_id}`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium text-white ${languageColors[enrollment.course?.language] || 'bg-gray-500'}`}>
                          {enrollment.course?.language?.toUpperCase()}
                        </span>
                        {isCompleted && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-500">
                            Completed
                          </span>
                        )}
                        {isExamPending && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-500">
                            Exam Ready
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {enrollment.course?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {enrollment.course?.description}
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{Math.round(enrollment.progress || 0)}%</span>
                        </div>
                        <Progress value={enrollment.progress || 0} className="h-2" />
                      </div>
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {enrollment.course?.chapters_data?.length || 7} chapters
                        </span>
                        <Button size="sm" className={`rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${isExamPending ? 'bg-amber-500 hover:bg-amber-600' : 'btn-primary'}`}>
                          {isExamPending ? (
                            <>
                              <Award className="w-4 h-4 mr-1" />
                              Take Exam
                            </>
                          ) : isCompleted ? (
                            <>
                              <Trophy className="w-4 h-4 mr-1" />
                              Review
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Continue
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Exam & Competitive Modules - Quick Access */}
        {['org_student', 'independent_student'].includes(user?.role) && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Exam Preparation</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {isEnabled('module_board_exams') && (
                <Link to="/exam-module" className="course-card group" data-testid="exam-module-card">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Board Exams</h3>
                  <p className="text-sm text-muted-foreground">AI-generated MCQ & Written exams for {user?.board || 'your board'} Class {user?.grade || ''} syllabus with instant grading.</p>
                </Link>
              )}
              {isEnabled('module_competitive_exams') && (
                <Link to="/competitive-exams" className="course-card group" data-testid="competitive-exams-card">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Competitive Exams</h3>
                  <p className="text-sm text-muted-foreground">Browse 50+ exams like JEE, NEET, CLAT, UPSC with practice questions and exam details.</p>
                </Link>
              )}
              {isEnabled('module_visual_labs') && (
                <Link to="/labs" className="course-card group" data-testid="visual-labs-card">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Visual Labs</h3>
                  <p className="text-sm text-muted-foreground">Interactive labs for Physics, Chemistry, Math, and more with hands-on simulations.</p>
                </Link>
              )}
              {isEnabled('module_analytics') && (
                <Link to="/analytics" className="course-card group" data-testid="analytics-card">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">My Analytics</h3>
                  <p className="text-sm text-muted-foreground">Track your performance with score trends, subject strengths, activity heatmap, and more.</p>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions for Admins */}
        {isAdmin && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {user?.role === 'super_admin' && (
              <>
                <Link to="/admin/schools" className="course-card group">
                  <div className="stat-card-icon bg-primary/10 mb-4">
                    <GraduationCap className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Manage Schools</h3>
                  <p className="text-sm text-muted-foreground">Add, edit, or remove schools from the platform.</p>
                </Link>
                <Link to="/admin/users" className="course-card group">
                  <div className="stat-card-icon bg-secondary/10 mb-4">
                    <User className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-semibold mb-2">Manage Users</h3>
                  <p className="text-sm text-muted-foreground">View and manage all users and their roles.</p>
                </Link>
              </>
            )}
            <Link to="/admin/courses" className="course-card group">
              <div className="stat-card-icon bg-green-500/10 mb-4">
                <BookOpen className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="font-semibold mb-2">Manage Courses</h3>
              <p className="text-sm text-muted-foreground">Create and edit courses and chapters.</p>
            </Link>
            <Link to="/admin/requests" className="course-card group">
              <div className="stat-card-icon bg-orange-500/10 mb-4">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2">Course Requests</h3>
              <p className="text-sm text-muted-foreground">Review and approve student course requests.</p>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
